import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { Control } from 'react-hook-form';
import { Controller, FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form';
// oxlint-disable-next-line no-restricted-imports
import { useMatch, useParams } from 'react-router';
import { useAccountContext } from '@gonfalon/app-context/useAccountContext';
import { userId } from '@gonfalon/constants/constants';
import { getDocumentationUrl } from '@gonfalon/docs/documentationUrl';
import { getDogfoodClient } from '@gonfalon/dogfood-client/getDogfoodClient';
import {
  enableWarehouseNativeMetricFilters,
  metricCreateModalSections,
  metricWindowingFrontend,
  warehouseHostedMetricsSupportPercentiles,
} from '@gonfalon/dogfood-flags/dogfood-flags';
import { ErrorBoundary } from '@gonfalon/error-boundaries/ErrorBoundary';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { trackMetricsEvent } from '@gonfalon/metrics/analytics';
import { DataSourceType, SNOWFLAKE_EXPERIMENTATION_INTEGRATION_KEY } from '@gonfalon/metrics/constants';
import { isLDHosted } from '@gonfalon/metrics/isLDHosted';
import { HealthIndicator } from '@gonfalon/metrics/MetricHealthCheck';
import { MetricKind } from '@gonfalon/metrics/MetricKind';
import { ratioAndDuaEnabledFor } from '@gonfalon/metrics/ratioAndDuaEnabledFor';
import { toMetricEventUrl } from '@gonfalon/metrics/toMetricEventUrl';
import type { Metric, MetricDataSource, MetricEventUrl } from '@gonfalon/metrics/types';
import { NavigationPromptModal } from '@gonfalon/navigation-prompt/modal';
import { genericCheckAccess } from '@gonfalon/permissions/access';
import { getMetricDataSourcesQuery } from '@gonfalon/rest-api/queries/metricDataSource';
import { getMetricEventsQuery } from '@gonfalon/rest-api/queries/metricEvents';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import {
  Alert,
  Button,
  Dialog,
  Form,
  Heading,
  Label,
  Link,
  ListBox,
  ListBoxItem,
  Modal,
  ModalOverlay,
  Popover,
  ProgressBar,
  Select,
  SelectValue,
  ToggleButton,
  ToggleButtonGroup,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { merge } from 'lodash';

import { SEARCH_RESULT_DISPLAY_NUMBER } from '../internal/CreateMetricModal/EventActivitySearchResults/EventActivitySearchResults';
import { toRatioWindowOffsets } from '../internal/CreateMetricModal/RatioTermSection';
import { supportsValueColumn } from '../internal/CreateMetricModal/ValueColumnField';

import { RequiredLabel } from './components/RequiredLabel';
import { WinsorizationAndWindowingSections } from './components/WinsorizationAndWindowingSections';
import { EventSourceField } from './EventSourceField';
import { toSubmittedEventKey } from './formUtils';
import type { DenominatorInput, MetricForm } from './formUtils';
import { MetricDataSourceFields } from './MetricDataSourceFields';
import { splitFilterByCategory } from './MetricEventQueryBuilder/eventQueryConversion';
import { MetricFooterFields } from './MetricFooterFields';
import { MetricKindDetails } from './MetricKindDetails';
import { MetricMetadata } from './MetricMetadata';
import { MetricWindowingParams, toWindowOffsets } from './MetricWindowingParams/MetricWindowingParams';
import { RatioMetricSection } from './RatioMetricSection';
import { useCreateMetric } from './useCreateMetric';
import { useIsRatioBlockedForUnsupportedWarehouse } from './useIsRatioBlockedForUnsupportedWarehouse';
import { WinsorizationFields } from './WinsorizationFields/WinsorizationFields';

import styles from './CreateMetricModal.module.css';

/*
   The sectioned layout opens on Count instead of the "Select" placeholder, so the definition
   sentence reads as a complete statement from the moment the modal opens. The values mirror
   exactly what choosing Count in the picker sets, so the posted metric is unchanged. Flag-off
   keeps the empty picker.
*/
const sectionedAggregationDefaults = (): Partial<MetricForm> =>
  metricCreateModalSections() ? { measureType: 'count', unitAggregationType: 'sum', isNumeric: false } : {};

// need to include every field or `isDirty` may be a false positive
const staticDefaultFormValues: MetricForm = {
  analysisType: 'mean',
  kind: MetricKind.CUSTOM,
  isNumeric: false,
  urls: [{ kind: 'canonical', value: '' }],
  analysisUnits: [],
  maintainerId: '',
  name: '',
  key: '',
  description: '',
  eventKey: '',
  tags: undefined,
  successCriteria: 'HigherThanBaseline',
  unitAggregationType: undefined,
  percentileValue: undefined,
  eventDefault: { disabled: false, value: 0 },
  dataSourceKey: DataSourceType.LAUNCHDARKLY_HOSTED,
  windowMode: 'none',
  windowUnit: 'days',
  windowStart: undefined,
  windowEnd: undefined,
  ratioOrSimple: 'simple',
  unitAggregationField: undefined,
  valueColumn: undefined,
  denominator: undefined,
  winsorization: undefined,
};

export function CreateMetricModal({
  onCancel,
  onSuccess,
  defaultValues,
  canChangeDataSource = true,
}: {
  onCancel: () => void;
  onSuccess?: (metric: Metric) => void;
  defaultValues?: Partial<MetricForm>;
  canChangeDataSource?: boolean;
}) {
  const projectKey = useProjectKey();
  const client = useQueryClient();
  const openAtStamp = useRef(Date.now());
  const { profile } = useAccountContext();
  const hasAccess = genericCheckAccess('metric', profile, 'createMetric', profile._access).isAllowed;

  const [showPrompt, setShowPrompt] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [closeMethod, setCloseMethod] = useState<'cancel button' | undefined>(undefined);
  const { mutate: createMetric, isPending: isSaving } = useCreateMetric();
  const form = useForm<MetricForm>({
    defaultValues: merge({}, staticDefaultFormValues, sectionedAggregationDefaults(), {
      maintainerId: userId(),
      ...defaultValues,
    }),
    mode: 'all',
  });

  const { isDirty, isValid, dirtyFields } = form.formState;
  const hasErrors = Boolean(Object.keys(form.formState.errors).length);
  const params = useParams();
  const matchBuilder = useMatch({ path: '/projects/:projKey/experiments/create' });
  const matchMetrics = useMatch({ path: '/projects/:projKey/metrics/new' });
  const matchMetricDataSource = useMatch({
    path: '/projects/:projKey/data-sources/metrics/:dataSourceKey/connections/new-metric',
  });
  const matchObserve = useMatch({ path: '/projects/:projKey/observe/*' });
  const matchHoldouts = useMatch({ path: '/projects/:projKey/holdouts/*' });

  const getLocation = () => {
    if (params.experimentKey) {
      return 'design';
    }
    if (matchBuilder) {
      return 'builder';
    }
    if (matchObserve) {
      return 'eventExplorer';
    }
    if (matchMetrics) {
      return 'metricsList';
    }
    if (matchMetricDataSource) {
      return 'metricDataSource';
    }
    if (matchHoldouts) {
      return 'holdouts';
    }
    return 'unknown';
  };

  const control = form.control;
  useEffect(() => {
    if (hasAccess) {
      trackMetricsEvent('Create Metric Modal Opened', {
        location: getLocation(),
      });
    }
  }, []);

  // Allow the parent component to pass in defaultValues on a subsequent render.
  // This handles the case where defaultValues are sourced from a network request,
  // meaning the defaultValue prop is undefined on the first render.
  const prevDefaultValues = useRef(defaultValues);
  useEffect(() => {
    // Only merge in deferred default values once,
    if (prevDefaultValues.current === undefined && typeof defaultValues === 'object') {
      for (const [key, defaultValue] of Object.entries(defaultValues)) {
        const fieldKey = key as keyof MetricForm;

        // Taking care not to overwrite any fields the user has already modified
        if (!dirtyFields[fieldKey]) {
          form.setValue(fieldKey, defaultValue);
        }
      }
    }

    prevDefaultValues.current = defaultValues;
  }, [form, dirtyFields, defaultValues]);

  const isMetricWindowingEnabled = metricWindowingFrontend();
  const metricKind = useWatch({ control, name: 'kind', exact: true });
  const ratioOrSimple = useWatch({ control, name: 'ratioOrSimple', exact: true }) ?? 'simple';
  const dataSourceKeyFormValue = useWatch({ control, name: 'dataSourceKey' });
  const { data: dataSourcesData } = useQuery(getMetricDataSourcesQuery({ projKey: projectKey }));

  const isRatioMetricsEnabled = useMemo(
    () => (dataSourcesData?.items ?? []).some((ds) => ratioAndDuaEnabledFor(ds.integrationKey)),
    [dataSourcesData],
  );

  const isRatioMode = isRatioMetricsEnabled && metricKind === MetricKind.CUSTOM && ratioOrSimple === 'ratio';

  // When entering ratio mode, auto-switch away from LD-hosted to the first available warehouse source
  useEffect(() => {
    if (isRatioMode && isLDHosted(dataSourceKeyFormValue)) {
      const firstWarehouse = dataSourcesData?.items?.find((ds) => ratioAndDuaEnabledFor(ds.integrationKey));
      if (firstWarehouse) {
        handleEventSourceChange({ dataSourceKey: firstWarehouse.key, kind: MetricKind.CUSTOM });
      }
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isRatioMode, dataSourcesData]);

  // When entering ratio mode the simple-form fields (measureType, unitAggregationType) unmount
  // but React Hook Form keeps their `required` rules registered by default, leaving `isValid`
  // false with no visible field to fix. Explicitly unregister them so the form can be submitted.
  useEffect(() => {
    if (isRatioMode) {
      form.unregister(['measureType', 'unitAggregationType']);
    }
  }, [isRatioMode, form]);
  const isSnowflakeExperimentationSource = useMemo(
    () => isSnowflakeExperimentationDataSource(dataSourcesData?.items, dataSourceKeyFormValue),
    [dataSourcesData, dataSourceKeyFormValue],
  );

  const ratioBlockedForUnsupportedWarehouse = useIsRatioBlockedForUnsupportedWarehouse({ control, isRatioMode });

  const useSectionedLayout = metricCreateModalSections();
  const isMeasurementWindowAvailable = isMetricWindowingEnabled && isSnowflakeExperimentationSource;
  // Kept as a fragment so the flat layout's DOM — two siblings of the form column — is unchanged.
  const flatWinsorizationAndWindowing = (
    <>
      {isMeasurementWindowAvailable && <MetricWindowingParams context={form} />}
      <WinsorizationFields context={form} />
    </>
  );
  const winsorizationAndWindowingSections = (
    <WinsorizationAndWindowingSections context={form} isMeasurementWindowAvailable={isMeasurementWindowAvailable} />
  );

  const eventKeyFormValue = useWatch({ control, name: 'eventKey' });
  const isNumericFormValue = useWatch({ control, name: 'isNumeric' });
  const measureTypeFormValue = useWatch({ control, name: 'measureType' });
  const unitAggregationTypeFormValue = useWatch({ control, name: 'unitAggregationType' });
  const successCriteriaFormValue = useWatch({
    control,
    name: 'successCriteria',
    exact: true,
  });

  const formAnalysisUnits = useWatch({
    control,
    name: 'analysisUnits',
    exact: true,
  }) as Array<{ value: string }>;

  const analysisUnits = formAnalysisUnits?.map((u) => u.value) || [];

  const handleEventSourceChange = useCallback(
    ({
      dataSourceKey,
      kind,
    }: {
      dataSourceKey: MetricForm['dataSourceKey'];
      kind: Extract<MetricKind, MetricKind.TRACE | MetricKind.CUSTOM>;
    }) => {
      if (kind !== MetricKind.TRACE) {
        form.clearErrors('traceQuery');
        form.setValue('traceQuery', undefined);
        form.setValue('traceValueLocation', undefined);
      }

      if (isLDHosted(dataSourceKey)) {
        form.clearErrors('dataSourceKey');
        form.setValue('windowMode', 'none');
        form.setValue('windowStart', undefined);
        form.setValue('windowEnd', undefined);
        // Target-column count_distinct is warehouse-native only. Switching to an
        // LD-hosted source hides the option from the picker, so clear the
        // selection too; otherwise a warehouse-only aggregation would remain
        // submittable for an LD-hosted source.
        if (form.getValues('unitAggregationType') === 'count_distinct') {
          form.setValue('measureType', undefined, { shouldValidate: true });
          form.setValue('unitAggregationType', undefined, { shouldValidate: true });
          form.setValue('isNumeric', false);
        }
      } else if (!warehouseHostedMetricsSupportPercentiles()) {
        // custom is the only kind supported for non-LD data sources. When warehouse percentiles are
        // unsupported, drop a stale percentile selection; when they're supported it should carry over.
        form.setValue('analysisType', 'mean');
      }
      // reset the eventKey, as its unlikely there will be a matching key in the
      // alternative data source
      form.setValue('eventKey', '');
      form.setValue('valueColumn', undefined);
      // the count_distinct target column belongs to the previous source's schema
      // and won't exist on the new one, so drop it on any source change.
      form.setValue('unitAggregationField', undefined);
      form.setValue('kind', kind);
      form.setValue('dataSourceKey', dataSourceKey);
      form.reset(form.getValues(), { keepValues: true, keepDirty: true, keepTouched: true });
    },
    [form],
  );

  const onValid = (metric: MetricForm) => {
    createMetric(toMetricPost(metric, dataSourcesData?.items), {
      onSuccess: (m) => {
        const successAtStamp = Date.now();
        const modalUseDuration = (successAtStamp - openAtStamp.current) / 1000;
        getDogfoodClient().track('Metric Creation Modal Duration', undefined, modalUseDuration);
        if (onSuccess) {
          onSuccess(m);
          trackLastSeenEventCount();
          trackMetricsEvent('Metric Creation Success', {
            metricKey: m.key,
            location: getLocation(),
          });
          if (m.filters) {
            trackMetricsEvent('Metric Created With Filters', {
              metricKey: m.key,
              filters: m.filters,
              eventKey: m.eventKey,
            });
          }
          setIsModalOpen(false);
          return;
        }
        onCancel();
      },
      onError: (error) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        trackMetricsEvent('Metric Creation Error', {
          location: getLocation(),
          message: errorMessage,
          metric,
        });
      },
    });
    trackMetricsEvent('Create Metric Modal Submitted', {
      metricKey: metric.key,
      location: getLocation(),
    });
  };

  const trackLastSeenEventCount = () => {
    const data = client.getQueryData(
      getMetricEventsQuery({
        projectKey,
        query: {
          q: eventKeyFormValue,
          limit: SEARCH_RESULT_DISPLAY_NUMBER,
          datasource: dataSourceKeyFormValue ?? 'launchdarkly-hosted',
        },
      }).queryKey,
    );

    if (data) {
      trackMetricsEvent('Events Last Fetched Count', {
        metricValue: data.items.length,
        value: data.items.length,
      });
    }
  };
  const onCancelWithPrompt = (closeLocation?: 'cancel button') => {
    if (isDirty) {
      setShowPrompt(true);
      setCloseMethod(closeLocation);
    } else {
      const closeLoc = closeLocation || closeMethod;
      handleModalExit(closeLoc);
      setCloseMethod(undefined);
    }
  };

  const handlePromptCancel = () => {
    setShowPrompt(false);
    setCloseMethod(undefined);
  };

  const handlePromptComfirm = () => {
    handleModalExit();
  };

  const handleModalExit = (closeLocation?: 'cancel button') => {
    const closeLoc = closeLocation || closeMethod;
    trackMetricsEvent('Create Metric Modal Closed', {
      location: getLocation(),
      closeMethod: closeLoc || 'background click',
    });
    setIsModalOpen(false);
    onCancel();
  };
  const formId = useId();

  return (
    <FormProvider {...form}>
      <ModalOverlay isOpen={isModalOpen} onOpenChange={(open: boolean) => !open && !showPrompt && onCancelWithPrompt()}>
        <Modal className={isRatioMode ? styles.noPaddingRatio : styles.noPadding}>
          <Dialog
            className={clsx(
              isRatioMode ? styles.noPaddingRatio : styles.noPadding,
              useSectionedLayout && styles.dialogFlush,
            )}
            data-test-id="create-metric-modal"
          >
            {showPrompt ? (
              <NavigationPromptModal
                onCancelNavigation={handlePromptCancel}
                onConfirmNavigation={handlePromptComfirm}
                body={msg('metrics.createModal.unsavedChangesBody')}
                confirmText={msg('metrics.createModal.discardChanges')}
              />
            ) : null}
            <div slot="header" className={styles.header}>
              <Heading slot="title" className={styles.formTitle}>
                <Message id="metrics.createModal.heading" />
              </Heading>
              <div className={styles.learnMoreLink}>
                <Message
                  id="metrics.createModal.intro"
                  values={{
                    link: (chunks) => (
                      <Link
                        href={getDocumentationUrl('home/observability/metrics')}
                        target="_blank"
                        className={styles.learnMoreLink}
                      >
                        {chunks}
                      </Link>
                    ),
                  }}
                />
              </div>
              {!hasAccess && (
                <Alert isOpen status="error" className={styles.permissionAlert}>
                  <h3>
                    <Message id="metrics.createModal.creationDisabledHeading" />
                  </h3>
                  <span>
                    <Message id="metrics.createModal.creationDisabledBody" />
                  </span>
                </Alert>
              )}
            </div>
            <div slot="body" className={clsx(styles.formBody, useSectionedLayout && styles.formBodyPadded)}>
              <ErrorBoundary
                severity="high"
                owner="team-agent-platform"
                fallback={
                  <Alert isOpen status="error">
                    <h3>
                      <Message id="metrics.createModal.loadErrorHeading" />
                    </h3>
                    <span>
                      <Message id="metrics.createModal.loadErrorBody" />
                    </span>
                  </Alert>
                }
              >
                <Form
                  id={formId}
                  className={styles.createMetricForm}
                  data-test-id="metric-form"
                  onSubmit={form.handleSubmit(onValid)}
                >
                  {isRatioMode && (
                    <div className={styles.ratioModeForm}>
                      {isRatioMetricsEnabled && metricKind === MetricKind.CUSTOM && (
                        <RatioOrSimpleMetricToggle control={control} />
                      )}
                      <EventSourceField
                        control={control}
                        onChange={handleEventSourceChange}
                        isDisabled={!canChangeDataSource}
                      />
                      <RatioMetricSection
                        context={form}
                        useSectionedLayout={useSectionedLayout}
                        canChangeDataSource={canChangeDataSource}
                      />
                      <MetricMetadata context={form} fullWidth />
                    </div>
                  )}
                  {!isRatioMode && (
                    <div className={styles.simpleModeForm}>
                      {isRatioMetricsEnabled && metricKind === MetricKind.CUSTOM && (
                        <RatioOrSimpleMetricToggle control={control} />
                      )}
                      <EventSourceField
                        control={control}
                        onChange={handleEventSourceChange}
                        isDisabled={!canChangeDataSource}
                      />
                      <MetricDataSourceFields
                        control={control}
                        showCreateCTA={canChangeDataSource}
                        isDisabled={!canChangeDataSource}
                      />
                      {isLDHosted(dataSourceKeyFormValue) && metricKind !== 'trace' && (
                        <MetricKindPicker control={control} />
                      )}
                      <MetricKindDetails
                        context={form}
                        metricKind={metricKind}
                        // Sectioned layout: these settings belong with the source configuration, so
                        // hand them to the kind details to render above the definition band.
                        winsorizationAndWindowing={useSectionedLayout ? winsorizationAndWindowingSections : undefined}
                        useSectionedLayout={useSectionedLayout}
                      />
                      {!useSectionedLayout && flatWinsorizationAndWindowing}
                      <MetricMetadata context={form} fullWidth={useSectionedLayout} />
                    </div>
                  )}
                </Form>
              </ErrorBoundary>
            </div>
            <div slot="footer" className={styles.footer}>
              <MetricFooterFields context={form} />
              <div className={styles.footerActions}>
                {metricKind === MetricKind.CUSTOM && !isRatioMode && (
                  <HealthIndicator
                    metric={{
                      kind: metricKind,
                      eventKey: eventKeyFormValue || '',
                      dataSource: dataSourceKeyFormValue ? { key: dataSourceKeyFormValue } : undefined,
                      analysisUnits:
                        ((measureTypeFormValue && measureTypeFormValue !== 'value') ||
                          !!unitAggregationTypeFormValue) &&
                        analysisUnits.length > 0
                          ? analysisUnits
                          : [],
                      isNumeric: !!isNumericFormValue || false,
                      _versionId: '',
                      successCriteria: successCriteriaFormValue,
                      unitAggregationType: unitAggregationTypeFormValue,
                    }}
                  />
                )}
                <Button onPress={() => onCancelWithPrompt('cancel button')}>{msg('metrics.createModal.cancel')}</Button>
                <Button
                  form={formId}
                  variant="primary"
                  type="submit"
                  isDisabled={hasErrors || !isValid || isSaving || !hasAccess || ratioBlockedForUnsupportedWarehouse}
                  data-test-id="metric-create-button"
                >
                  {msg('metrics.createModal.create')}
                  {isSaving && <ProgressBar aria-label={msg('metrics.status.loading')} isIndeterminate size="small" />}
                </Button>
              </div>
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </FormProvider>
  );
}

interface RatioOrSimpleMetricToggleProps {
  control: Control<MetricForm>;
}

export const RatioOrSimpleMetricToggle: React.FC<RatioOrSimpleMetricToggleProps> = ({ control }) => {
  const ratioOrSimple = useWatch({ control, name: 'ratioOrSimple', exact: true }) ?? 'simple';
  const { getValues, setValue } = useFormContext<MetricForm>();

  return (
    <Controller
      control={control}
      name="ratioOrSimple"
      render={({ field }) => (
        <div className={styles.ratioOrSimpleTogglePicker}>
          <Label>
            <Message id="metrics.createModal.structureLabel" />
          </Label>
          <ToggleButtonGroup
            selectedKeys={[ratioOrSimple]}
            onSelectionChange={(keys) => {
              const newType = [...keys][0];
              if (newType === 'simple' || newType === 'ratio') {
                field.onChange(newType);
                if (getValues('dataSourceKey') === undefined) {
                  setValue('dataSourceKey', DataSourceType.LAUNCHDARKLY_HOSTED);
                }
              }
            }}
            disallowEmptySelection
            className={styles.ratioOrSimpleToggle}
          >
            <ToggleButton id="simple">{msg('metrics.createModal.simple')}</ToggleButton>
            <ToggleButton id="ratio">{msg('metrics.createModal.ratio')}</ToggleButton>
          </ToggleButtonGroup>
        </div>
      )}
    />
  );
};

interface MetricKindPickerProps {
  control: Control<MetricForm>;
}

const MetricKindPicker: React.FC<MetricKindPickerProps> = ({ control }) => {
  const dataSourceKeyFormValue = useWatch({ control, name: 'dataSourceKey' });

  return (
    <Controller
      control={control}
      name="kind"
      render={({ field }) => (
        <Select
          onChange={field.onChange}
          value={field.value}
          className={styles.shortInput}
          isDisabled={!isLDHosted(dataSourceKeyFormValue)}
        >
          <RequiredLabel label={msg('metrics.createModal.eventKindLabel')} htmlFor="kind" />
          <Button>
            <SelectValue />
            <Icon name="chevron-down" size="small" />
          </Button>
          <Popover>
            <ListBox>
              <ListBoxItem id={MetricKind.CUSTOM}>{msg('metrics.createModal.custom')}</ListBoxItem>
              <ListBoxItem id={MetricKind.PAGEVIEW}>{msg('metrics.createModal.pageViewed')}</ListBoxItem>
              <ListBoxItem id={MetricKind.CLICK}>{msg('metrics.createModal.clickedOrTapped')}</ListBoxItem>
            </ListBox>
          </Popover>
        </Select>
      )}
    />
  );
};

export type MetricPost = Omit<
  MetricForm,
  | 'tags'
  | 'analysisUnits'
  | 'urls'
  | 'kind'
  | 'windowMode'
  | 'windowUnit'
  | 'windowStart'
  | 'windowEnd'
  | 'ratioOrSimple'
  | 'denominator'
  | 'dataSourceKey'
  | 'winsorization'
> & {
  kind: 'click' | 'pageview' | 'custom' | 'trace';
  tags?: string[];
  analysisUnits?: string[];
  urls?: MetricEventUrl[];
  windowStartOffset?: number;
  windowEndOffset?: number;
  denominator?: DenominatorInput;
  dataSource?: { key: string };
  isActive: boolean;
  winsorLowerPercentile?: number;
  winsorUpperPercentile?: number;
};

export function isSnowflakeExperimentationDataSource(
  dataSources: MetricDataSource[] | undefined,
  dataSourceKey?: string,
): boolean {
  return (
    dataSources?.find((ds) => ds.key === dataSourceKey)?.integrationKey === SNOWFLAKE_EXPERIMENTATION_INTEGRATION_KEY
  );
}

export function toMetricPost(form: MetricForm, dataSources?: MetricDataSource[]): MetricPost {
  const {
    kind,
    urls,
    analysisUnits,
    tags,
    dataSourceKey,
    measureType: _measureType,
    windowMode,
    windowUnit,
    windowStart,
    windowEnd,
    ratioOrSimple: _ratioOrSimple,
    denominator: denominatorForm,
    winsorization,
    valueColumn,
    ...rest
  } = form;

  const isRatio = form.ratioOrSimple === 'ratio';
  const postedValueColumn = supportsValueColumn(dataSourceKey) && valueColumn ? valueColumn : undefined;

  const metric = {
    ...rest,
    eventKey: toSubmittedEventKey(form),
    isActive: true,
    kind,
    analysisUnits: analysisUnits?.map(({ value }) => value),
    tags: tags?.map(({ value }) => value),
    urls: kind === MetricKind.CLICK || kind === MetricKind.PAGEVIEW ? urls?.map(toMetricEventUrl) : undefined,
    filters: isLDHosted(form.dataSourceKey)
      ? form.filters
      : enableWarehouseNativeMetricFilters() && isSnowflakeExperimentationDataSource(dataSources, form.dataSourceKey)
        ? splitFilterByCategory(form.filters).eventFilter
        : undefined,
    dataSource: dataSourceKey ? { key: dataSourceKey } : undefined,
    traceValueLocation: kind === 'trace' && form.isNumeric ? form.traceValueLocation : undefined,
    ...(windowMode === 'none'
      ? { windowStartOffset: undefined, windowEndOffset: undefined }
      : toWindowOffsets(windowStart, windowEnd, windowUnit)),
    unitAggregationField: `${rest.unitAggregationType}` === 'count_distinct' ? rest.unitAggregationField : undefined,
    valueColumn: postedValueColumn,
    denominator:
      isRatio && kind === MetricKind.CUSTOM && denominatorForm
        ? buildDenominatorPost(denominatorForm, dataSources)
        : undefined,
    // Map UI winsorization fields to API format
    // Drop 0 and 100 values (they mean "no winsorization").
    // count_distinct is incompatible with winsorization — guard at serialization as a safety net.
    winsorLowerPercentile:
      `${rest.unitAggregationType}` !== 'count_distinct' &&
      winsorization?.lowerPercentile !== undefined &&
      winsorization.lowerPercentile !== 0
        ? winsorization.lowerPercentile
        : undefined,
    winsorUpperPercentile:
      `${rest.unitAggregationType}` !== 'count_distinct' &&
      winsorization?.upperPercentile !== undefined &&
      winsorization.upperPercentile !== 100
        ? winsorization.upperPercentile
        : undefined,
  };

  if (kind === MetricKind.CUSTOM) {
    metric.isNumeric = !!metric.isNumeric;
  }

  if (isRatio) {
    metric.unit = undefined;
    metric.eventDefault = { disabled: false, value: 0 };
  }

  return metric;
}

export function buildDenominatorPost(
  d: NonNullable<MetricForm['denominator']>,
  dataSources?: MetricDataSource[],
): DenominatorInput {
  const windowOffsets = toRatioWindowOffsets(d.windowMode, d.windowStart, d.windowEnd, d.windowUnit);
  const postedValueColumn = supportsValueColumn(d.dataSourceKey) && d.valueColumn ? d.valueColumn : undefined;

  return {
    eventName: toSubmittedEventKey(d) ?? '',
    isNumeric: d.aggregationType === 'sum' || d.aggregationType === 'average',
    unitAggregationType: d.aggregationType === 'count' ? 'sum' : d.aggregationType,
    unitAggregationField: d.aggregationType === 'count_distinct' ? d.unitAggregationField : undefined,
    valueColumn: postedValueColumn,
    // Ratio terms are always warehouse-native, so only the event-property half is sendable, and only
    // for a snowflake-experimentation source — the only one that offers a filter editor.
    filters:
      enableWarehouseNativeMetricFilters() && isSnowflakeExperimentationDataSource(dataSources, d.dataSourceKey)
        ? splitFilterByCategory(d.filters).eventFilter
        : undefined,
    dataSource: d.dataSourceKey ? { key: d.dataSourceKey } : undefined,
    ...windowOffsets,
    // count_distinct is incompatible with winsorization — guard at serialization as a safety net
    // in case form state was not cleared before submission.
    winsorLowerPercentile:
      d.aggregationType !== 'count_distinct' && d.winsorLowerEnabled && d.winsorLowerPercentile !== 0
        ? d.winsorLowerPercentile
        : undefined,
    winsorUpperPercentile:
      d.aggregationType !== 'count_distinct' && d.winsorUpperEnabled && d.winsorUpperPercentile !== 100
        ? d.winsorUpperPercentile
        : undefined,
  };
}
